import React, { useState, useEffect, useCallback } from 'react';
import HeroSection from '../components/HeroSection';
import axios from "axios";
import config from '../config/config';
import { X, Plus, Trash2, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HeroAdmin = ({ config: initialConfig = {}, slug = '', onSave, onClose }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [preset, setPreset] = useState('split-layout');
  const [title, setTitle] = useState('Discover Your Perfect Style');
  const [subtitle, setSubtitle] = useState('Explore our latest collection...');
  const [badge, setBadge] = useState('New Collection 2025');
  const [ctaPrimary, setCtaPrimary] = useState({ text: 'Shop Now', link: '/shop' });
  const [ctaSecondary, setCtaSecondary] = useState({ text: 'View Details', link: '#' });
  const [stats, setStats] = useState([
    { value: '500+', label: 'Products' },
    { value: '50k+', label: 'Happy Customers' },
    { value: '4.9', label: 'Avg Rating' },
  ]);

  // Single product for product-showcase
  const [product, setProduct] = useState(null);

  // NEW: Multiple products for product-grid
  const [gridProducts, setGridProducts] = useState([]);

  const [devMode, setDevMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullScreenPreview, setFullScreenPreview] = useState(false);

  // Shared product modal state
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  // 'showcase' = picking for product-showcase, or a number index for product-grid slot
  const [productModalTarget, setProductModalTarget] = useState(null);

  const [container, setContainer] = useState({
    padding: 'py-16 md:py-24',
    background: 'bg-gradient-to-br from-gray-50 to-white',
    heroImage: '',
    backgroundImage: '',
    titleColor: '#111827',
    subtitleColor: '#4B5563',
    badgeBg: '#f3f4f6',
    badgeText: '#111827',
    ctaPrimaryBg: '#111827',
    ctaPrimaryText: '#ffffff',
    ctaSecondaryBorder: '#6b7280',
    ctaSecondaryText: '#374151',
  });

  // ─── Load saved settings ────────────────────────────────────────────────────
  useEffect(() => {
    const loadHeroSettings = async () => {
      if (!token) { setLoading(false); return; }
      try {
        setLoading(true);
        await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        let response;
        const isDuplicate = slug.includes('-');
        if (isDuplicate) {
          response = await axios.get(
            `${config.API_URL}/api/pullCustomSettings/${slug}`,
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        } else {
          response = await axios.get(
            config.API_URL + '/api/pullHeroSettings',
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        }

        const dbData = isDuplicate
          ? (response.data.customSettings || {})
          : (response.data.heroSettings || {});

        setPreset(dbData.preset || 'split-layout');
        setTitle(dbData.title || title);
        setSubtitle(dbData.subtitle || subtitle);
        setBadge(dbData.badge || badge);
        setCtaPrimary(dbData.ctaPrimary || ctaPrimary);
        setCtaSecondary(dbData.ctaSecondary || ctaSecondary);
        setStats(dbData.stats || stats);
        setProduct(dbData.product || null);
        setGridProducts(dbData.products || []);
        setDevMode(dbData.devMode || false);
        setContainer(dbData.customStyles?.container || container);
      } catch (err) {
        console.error("Failed to load hero settings", err);
      } finally {
        setLoading(false);
      }
    };
    loadHeroSettings();
  }, [token, slug]);

  // ─── Fetch products when needed ─────────────────────────────────────────────
  useEffect(() => {
    if (!token || !['product-showcase', 'product-grid'].includes(preset)) return;
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${config.API_URL}/api/pullProducts/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAllProducts(res.data?.data || res.data || []);
      } catch (err) {
        console.error("Failed to load products", err);
      }
    };
    fetchProducts();
  }, [token, preset]);

  // ─── Save ────────────────────────────────────────────────────────────────────
  const saveHeroSettings = async () => {
    try {
      setSaving(true);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const endpoint = slug ? `/api/saveHeroSettings/${slug}` : '/api/saveHeroSettings';

      const payload = {
        preset,
        title,
        subtitle,
        badge,
        ctaPrimary,
        ctaSecondary,
        stats,
        product,
        products: gridProducts,
        devMode,
        customStyles: { container },
      };

      const response = await axios.post(
        config.API_URL + endpoint,
        { settings: payload },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 1) {
        alert("Hero saved successfully!");
        if (onSave) onSave(payload);
      } else {
        alert("Save failed.");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to save hero section.");
    } finally {
      setSaving(false);
    }
  };

  // ─── Stats helpers ──────────────────────────────────────────────────────────
  const addStat = () => setStats([...stats, { value: 'New', label: 'Label' }]);
  const updateStat = (index, field, value) => {
    const newStats = [...stats];
    newStats[index][field] = value;
    setStats(newStats);
  };
  const removeStat = (index) => setStats(stats.filter((_, i) => i !== index));

  // ─── Grid products helpers ──────────────────────────────────────────────────
  const addGridSlot = () => {
    if (gridProducts.length >= 6) return; // max 6 products in grid
    setGridProducts(prev => [...prev, null]);
  };

  const removeGridSlot = (index) => {
    setGridProducts(prev => prev.filter((_, i) => i !== index));
  };

  const moveGridProduct = (index, direction) => {
    const newArr = [...gridProducts];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newArr.length) return;
    [newArr[index], newArr[swapIdx]] = [newArr[swapIdx], newArr[index]];
    setGridProducts(newArr);
  };

  // ─── Product modal ──────────────────────────────────────────────────────────
  // target: 'showcase' or number (grid index)
  const openProductModal = (target) => {
    setProductModalTarget(target);
    if (target === 'showcase') {
      setSelectedProductId(product?.id || null);
    } else {
      setSelectedProductId(gridProducts[target]?.id || null);
    }
    setIsProductModalOpen(true);
  };

  const confirmSelectProduct = () => {
    const mapProduct = (p) => ({
      id: p.id,
      name: p.name,
      price: parseFloat(p.price) || 0,
      originalPrice: parseFloat(p.regular_price) || null,
      image: p.image_url || '',
      rating: p.rating || 4.8,
      reviews: p.reviews_count || 0,
    });

    if (productModalTarget === 'showcase') {
      if (!selectedProductId) {
        setProduct(null);
      } else {
        const selected = allProducts.find(p => p.id === selectedProductId);
        if (selected) setProduct(mapProduct(selected));
      }
    } else if (typeof productModalTarget === 'number') {
      const newArr = [...gridProducts];
      if (!selectedProductId) {
        newArr[productModalTarget] = null;
      } else {
        const selected = allProducts.find(p => p.id === selectedProductId);
        if (selected) newArr[productModalTarget] = mapProduct(selected);
      }
      setGridProducts(newArr);
    }

    setIsProductModalOpen(false);
    setProductModalTarget(null);
    setSelectedProductId(null);
  };

  // ─── Derived flags ──────────────────────────────────────────────────────────
  const isProductPreset = ['product-showcase', 'product-grid'].includes(preset);
  const showStats = ['split-layout', 'centered-dark', 'image-overlay'].includes(preset);

  // Already-selected IDs in grid (to highlight in modal)
  const selectedGridIds = gridProducts.filter(Boolean).map(p => p.id);

  // ─── Current config passed to preview ───────────────────────────────────────
  const currentConfig = {
    preset,
    title,
    subtitle,
    badge,
    ctaPrimary,
    ctaSecondary,
    stats: showStats ? stats : [],
    product: preset === 'product-showcase' ? product : undefined,
    products: preset === 'product-grid' ? gridProducts.filter(Boolean) : [],
    devMode,
    customStyles: { container },
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex">

      {/* ── Live Preview ─────────────────────────────────────────────────────── */}
      <div className="flex-1 relative">
        <div className="absolute inset-0 flex flex-col">
          <div className="bg-white border-b px-6 py-4 flex justify-between items-center shadow-sm">
            <h1 className="text-2xl font-bold text-gray-900">Hero Editor</h1>
            <button
              onClick={() => setFullScreenPreview(true)}
              className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition"
            >
              Full Preview
            </button>
          </div>
          <div className="flex-1 overflow-auto bg-gray-50 p-6">
            <HeroSection config={currentConfig} />
          </div>
        </div>
      </div>

      {/* ── Settings Sidebar ─────────────────────────────────────────────────── */}
      <div className="w-96 bg-white border-l shadow-2xl overflow-y-auto flex flex-col">
        <div className="p-6 space-y-6 flex-1">
          <h2 className="text-xl font-bold text-gray-900">Hero Settings</h2>

          {/* Preset */}
          <div>
            <h3 className="font-medium mb-2 text-sm text-gray-700">Layout Preset</h3>
            <div className="grid gap-2">
              {['split-layout', 'centered-dark', 'image-overlay', 'product-showcase', 'product-grid'].map(p => (
                <button
                  key={p}
                  onClick={() => setPreset(p)}
                  className={`p-3 border rounded-lg text-left text-sm transition ${
                    preset === p
                      ? 'border-indigo-500 bg-indigo-50 font-semibold text-indigo-800'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  {p.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </button>
              ))}
            </div>
          </div>

          {/* Title / Subtitle / Badge */}
          <div className="space-y-4 border-t pt-5">
            <div>
              <label className="block text-sm font-medium mb-1">Main Title</label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <textarea
                value={subtitle}
                onChange={e => setSubtitle(e.target.value)}
                className="w-full border rounded-md px-3 py-2 h-20 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Badge Text <span className="text-gray-400">(optional)</span></label>
              <input
                value={badge}
                onChange={e => setBadge(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm"
                placeholder="e.g. New Season"
              />
            </div>
          </div>

          {/* CTAs */}
          <div className="space-y-4 border-t pt-5">
            <div>
              <label className="block text-sm font-medium mb-1">Primary Button</label>
              <input
                value={ctaPrimary.text}
                onChange={e => setCtaPrimary({ ...ctaPrimary, text: e.target.value })}
                className="w-full border rounded px-3 py-2 mb-2 text-sm"
                placeholder="Button label"
              />
              <input
                value={ctaPrimary.link}
                onChange={e => setCtaPrimary({ ...ctaPrimary, link: e.target.value })}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="/shop or https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Secondary Button <span className="text-gray-400">(optional)</span></label>
              <input
                value={ctaSecondary.text}
                onChange={e => setCtaSecondary({ ...ctaSecondary, text: e.target.value })}
                className="w-full border rounded px-3 py-2 mb-2 text-sm"
                placeholder="Button label"
              />
              <input
                value={ctaSecondary.link}
                onChange={e => setCtaSecondary({ ...ctaSecondary, link: e.target.value })}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="# or external link"
              />
            </div>
          </div>

          {/* ── PRODUCT SHOWCASE ─────────────────────────────────────────────── */}
          {preset === 'product-showcase' && (
            <div className="border-t pt-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-sm">Featured Product</h3>
                <button
                  onClick={() => openProductModal('showcase')}
                  className="text-sm bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700 flex items-center gap-1 transition"
                >
                  <Plus size={14} /> {product ? 'Change' : 'Select'}
                </button>
              </div>
              {product ? (
                <div className="bg-gray-50 p-3 rounded border flex items-center gap-3">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-16 h-16 object-cover rounded border"
                    onError={e => e.target.src = 'https://placehold.co/64x64?text=No+Img'}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{product.name}</p>
                    <p className="text-xs text-gray-500">KES {product.price.toLocaleString()}</p>
                  </div>
                  <button onClick={() => setProduct(null)} className="text-red-500 hover:text-red-700">
                    <Trash2 size={15} />
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No product selected</p>
              )}
            </div>
          )}

          {/* ── PRODUCT GRID ─────────────────────────────────────────────────── */}
          {preset === 'product-grid' && (
            <div className="border-t pt-5">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="font-semibold text-sm">Grid Products</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{gridProducts.length} slot{gridProducts.length !== 1 ? 's' : ''} (max 6)</p>
                </div>
                <button
                  onClick={addGridSlot}
                  disabled={gridProducts.length >= 6}
                  className="text-sm bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1 transition"
                >
                  <Plus size={14} /> Add Slot
                </button>
              </div>

              {gridProducts.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                  <p className="text-sm text-gray-400">No products added yet.</p>
                  <p className="text-xs text-gray-400 mt-1">Click "Add Slot" to start building your grid.</p>
                </div>
              )}

              <div className="space-y-3">
                {gridProducts.map((prod, i) => (
                  <div
                    key={i}
                    className="bg-gray-50 border rounded-xl p-3 flex items-center gap-3"
                  >
                    {/* Order controls */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => moveGridProduct(i, -1)}
                        disabled={i === 0}
                        className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        onClick={() => moveGridProduct(i, 1)}
                        disabled={i === gridProducts.length - 1}
                        className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      >
                        <ArrowDown size={14} />
                      </button>
                    </div>

                    {/* Slot number */}
                    <span className="text-xs font-bold text-gray-400 w-4 text-center">{i + 1}</span>

                    {/* Product info or empty state */}
                    {prod ? (
                      <>
                        <img
                          src={prod.image}
                          alt={prod.name}
                          className="w-12 h-12 object-cover rounded border bg-white flex-shrink-0"
                          onError={e => e.target.src = 'https://placehold.co/48x48?text=?'}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-xs truncate">{prod.name}</p>
                          <p className="text-xs text-gray-400">KES {prod.price.toLocaleString()}</p>
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-400 italic">Empty slot</p>
                      </div>
                    )}

                    {/* Change / Select button */}
                    <button
                      onClick={() => openProductModal(i)}
                      className="text-xs text-indigo-600 border border-indigo-300 px-2 py-1 rounded hover:bg-indigo-50 transition flex-shrink-0"
                    >
                      {prod ? 'Change' : 'Pick'}
                    </button>

                    {/* Remove slot */}
                    <button
                      onClick={() => removeGridSlot(i)}
                      className="text-red-500 hover:text-red-700 flex-shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── STATS ────────────────────────────────────────────────────────── */}
          {showStats && (
            <div className="border-t pt-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-sm">Stats ({stats.length})</h3>
                <button
                  onClick={addStat}
                  className="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-3">
                {stats.map((stat, i) => (
                  <div key={i} className="bg-gray-50 p-3 rounded border flex gap-3 items-center">
                    <input
                      value={stat.value}
                      onChange={e => updateStat(i, 'value', e.target.value)}
                      className="w-20 border rounded px-2 py-1 text-center text-sm"
                      placeholder="500+"
                    />
                    <input
                      value={stat.label}
                      onChange={e => updateStat(i, 'label', e.target.value)}
                      className="flex-1 border rounded px-2 py-1 text-sm"
                      placeholder="Happy Customers"
                    />
                    <button onClick={() => removeStat(i)} className="text-red-500 hover:text-red-700">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── IMAGE URLs ───────────────────────────────────────────────────── */}
          <div className="border-t pt-5 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Hero / Product Image URL</label>
              <input
                type="url"
                value={container.heroImage}
                onChange={e => setContainer(prev => ({ ...prev, heroImage: e.target.value }))}
                placeholder="https://example.com/hero.jpg"
                className="w-full border rounded px-3 py-2 text-sm"
              />
              {container.heroImage && (
                <img
                  src={container.heroImage}
                  alt="Hero preview"
                  className="mt-2 w-24 h-24 rounded border"
                  onError={e => (e.target.style.display = 'none')}
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Background Image / Video URL <span className="text-gray-400">(optional)</span></label>
              <input
                type="url"
                value={container.backgroundImage}
                onChange={e => setContainer(prev => ({ ...prev, backgroundImage: e.target.value }))}
                placeholder="https://example.com/bg.jpg or bg.mp4"
                className="w-full border rounded px-3 py-2 text-sm"
              />
              {container.backgroundImage && !container.backgroundImage.match(/\.(mp4|webm|mov)$/i) && (
                <img
                  src={container.backgroundImage}
                  alt="Background preview"
                  className="mt-2 w-24 h-24 rounded border"
                  onError={e => (e.target.style.display = 'none')}
                />
              )}
            </div>
          </div>

          {/* ── DEV MODE ─────────────────────────────────────────────────────── */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-amber-900 text-sm">Custom Styles (Dev Mode)</h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={devMode}
                  onChange={() => setDevMode(!devMode)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-amber-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
              </label>
            </div>
            {devMode && (
              <div className="space-y-4 text-sm">
                <div>
                  <label className="block font-medium mb-1">Padding (Tailwind)</label>
                  <input
                    value={container.padding}
                    onChange={e => setContainer({ ...container, padding: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="py-16 md:py-24"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Background (Tailwind class or CSS value)</label>
                  <input
                    value={container.background}
                    onChange={e => setContainer({ ...container, background: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="bg-white or linear-gradient(...)"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium mb-1">Title Color</label>
                    <input type="color" value={container.titleColor}
                      onChange={e => setContainer({ ...container, titleColor: e.target.value })}
                      className="w-full h-10 rounded border cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Subtitle Color</label>
                    <input type="color" value={container.subtitleColor}
                      onChange={e => setContainer({ ...container, subtitleColor: e.target.value })}
                      className="w-full h-10 rounded border cursor-pointer"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium mb-1">Primary Btn Bg</label>
                    <input type="color" value={container.ctaPrimaryBg}
                      onChange={e => setContainer({ ...container, ctaPrimaryBg: e.target.value })}
                      className="w-full h-10 rounded border cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Primary Btn Text</label>
                    <input type="color" value={container.ctaPrimaryText}
                      onChange={e => setContainer({ ...container, ctaPrimaryText: e.target.value })}
                      className="w-full h-10 rounded border cursor-pointer"
                    />
                  </div>
                </div>
                {preset !== 'product-grid' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-medium mb-1">Badge Bg</label>
                      <input type="color" value={container.badgeBg}
                        onChange={e => setContainer({ ...container, badgeBg: e.target.value })}
                        className="w-full h-10 rounded border cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block font-medium mb-1">Badge Text</label>
                      <input type="color" value={container.badgeText}
                        onChange={e => setContainer({ ...container, badgeText: e.target.value })}
                        className="w-full h-10 rounded border cursor-pointer"
                      />
                    </div>
                  </div>
                )}
                {ctaSecondary.text && preset !== 'product-grid' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-medium mb-1">Secondary Border</label>
                      <input type="color" value={container.ctaSecondaryBorder}
                        onChange={e => setContainer({ ...container, ctaSecondaryBorder: e.target.value })}
                        className="w-full h-10 rounded border cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block font-medium mb-1">Secondary Text</label>
                      <input type="color" value={container.ctaSecondaryText}
                        onChange={e => setContainer({ ...container, ctaSecondaryText: e.target.value })}
                        className="w-full h-10 rounded border cursor-pointer"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Sticky Save ───────────────────────────────────────────────────── */}
        <div className="sticky bottom-0 bg-white border-t p-6 shadow-lg">
          <button
            onClick={saveHeroSettings}
            disabled={saving}
            className={`w-full py-4 rounded-xl font-bold text-white shadow transition ${
              saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {saving ? 'Saving...' : 'Save & Publish Hero'}
          </button>
        </div>
      </div>

      {/* ── Product Selection Modal ──────────────────────────────────────────── */}
      {isProductModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold">
                  {productModalTarget === 'showcase'
                    ? 'Select Featured Product'
                    : `Select Product for Slot ${typeof productModalTarget === 'number' ? productModalTarget + 1 : ''}`}
                </h2>
                {preset === 'product-grid' && typeof productModalTarget === 'number' && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {selectedGridIds.length} of 6 slots used
                  </p>
                )}
              </div>
              <button
                onClick={() => { setIsProductModalOpen(false); setProductModalTarget(null); }}
                className="text-gray-400 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {allProducts.map(p => {
                  const isSelected = selectedProductId === p.id;
                  // In grid mode, show if already used in another slot
                  const usedInOtherSlot = preset === 'product-grid'
                    && selectedGridIds.includes(p.id)
                    && gridProducts[productModalTarget]?.id !== p.id;

                  return (
                    <div
                      key={p.id}
                      onClick={() => !usedInOtherSlot && setSelectedProductId(p.id)}
                      className={`p-3 border rounded-lg flex gap-3 items-center transition-all ${
                        usedInOtherSlot
                          ? 'opacity-40 cursor-not-allowed bg-gray-50'
                          : 'cursor-pointer'
                      } ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                          : !usedInOtherSlot ? 'hover:border-gray-300 hover:bg-gray-50' : ''
                      }`}
                    >
                      <img
                        src={p.image_url || 'https://placehold.co/80x80?text=?'}
                        alt={p.name}
                        className="w-14 h-14 object-cover rounded border bg-gray-50 flex-shrink-0"
                        onError={e => e.target.src = 'https://placehold.co/80x80?text=No+Img'}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{p.name}</p>
                        <p className="text-xs text-gray-500">KES {(parseFloat(p.price) || 0).toLocaleString()}</p>
                        {usedInOtherSlot && (
                          <p className="text-xs text-orange-500 mt-0.5">Already in another slot</p>
                        )}
                      </div>
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {allProducts.length === 0 && (
                <p className="text-center text-gray-400 py-12 text-sm">No products found</p>
              )}
            </div>

            <div className="border-t px-6 py-4 flex justify-between items-center">
              {selectedProductId && (
                <button
                  onClick={() => setSelectedProductId(null)}
                  className="text-sm text-red-500 hover:text-red-700"
                >
                  Clear selection
                </button>
              )}
              <div className="flex gap-3 ml-auto">
                <button
                  onClick={() => { setIsProductModalOpen(false); setProductModalTarget(null); }}
                  className="px-5 py-2 border rounded-lg hover:bg-gray-100 text-sm transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSelectProduct}
                  className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm transition"
                >
                  {selectedProductId ? 'Confirm Selection' : 'Clear Slot'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Full Screen Preview ──────────────────────────────────────────────── */}
      {fullScreenPreview && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex flex-col"
          onClick={() => setFullScreenPreview(false)}
        >
          <div className="bg-white/10 backdrop-blur px-6 py-4 flex justify-between items-center">
            <span className="text-white text-sm opacity-60">Click outside to close</span>
            <button
              className="text-white text-4xl hover:text-gray-300 leading-none"
              onClick={() => setFullScreenPreview(false)}
            >
              ×
            </button>
          </div>
          <div className="flex-1 overflow-auto p-6 md:p-10" onClick={e => e.stopPropagation()}>
            <HeroSection config={currentConfig} />
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroAdmin;